export type TokenSegment = { type: 'literal'; value: string } | { type: 'variable'; value: string };

const VARIABLE_TOKEN_PATTERN = /\{\{\s*([^{}]*)\s*\}\}/g;

// Splits a template string into ordered literal/variable segments, e.g.
// `exports/{{audienceNameKebab}}/{{timestamp}}_file.csv` becomes
// [literal, variable(audienceNameKebab), literal, variable(timestamp), literal].
export const tokenize = (template: string): TokenSegment[] => {
  if (!template) return [];

  const segments: TokenSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  VARIABLE_TOKEN_PATTERN.lastIndex = 0;
  while ((match = VARIABLE_TOKEN_PATTERN.exec(template))) {
    if (match.index > lastIndex) {
      segments.push({ type: 'literal', value: template.slice(lastIndex, match.index) });
    }

    segments.push({ type: 'variable', value: match[1].trim() });
    lastIndex = VARIABLE_TOKEN_PATTERN.lastIndex;
  }

  if (lastIndex < template.length) {
    segments.push({ type: 'literal', value: template.slice(lastIndex) });
  }

  return segments;
};

// Names of every variable used in `template`, in order of appearance (duplicates included). The
// single source of truth for "does this template use variable X" — consumers use it both to
// validate a template and to detect variables that should change how the field behaves (e.g. lock
// it read-only), so that check can't drift out of sync with how templates are actually parsed.
export const getTemplateVariableNames = (template: string): string[] =>
  tokenize(template)
    .filter((segment): segment is Extract<TokenSegment, { type: 'variable' }> => segment.type === 'variable')
    .map((segment) => segment.value);

// Renders a preview of the resolved template using representative sample values, for a "sample
// output" caption a consumer can render below the input.
export const renderTemplatePreview = (template: string, sampleValues: Record<string, string>): string =>
  tokenize(template)
    .map((segment) =>
      segment.type === 'literal' ? segment.value : sampleValues[segment.value] ?? `{{${segment.value}}}`,
    )
    .join('');

export interface ActiveVariableToken {
  // index (within the checked text) of the `{` that opens the in-progress token.
  start: number;
  // partial variable name typed so far, after the `{`.
  query: string;
}

// Detects whether the caret sits inside an *open* `{variableName` token: there's a single `{`
// before the caret with no closing `}` in between, and everything between it and the caret is a
// plain partial variable name (letters only — no `/`, `}`, `{` or whitespace). Returns null
// otherwise. In the contentEditable editor this is run against the active text node's content
// (not the whole serialized value), since variable tokens already typed are chip elements, not
// text. The inserted/serialized token is still `{{name}}` — only the trigger keystroke is a
// single brace.
export const getActiveVariableToken = (text: string, caretPos: number): ActiveVariableToken | null => {
  if (!text) return null;

  const uptoCaret = text.slice(0, caretPos);
  const start = uptoCaret.lastIndexOf('{');
  if (start === -1) return null;

  const query = uptoCaret.slice(start + 1);
  if (!/^[a-zA-Z]*$/.test(query)) return null;

  return { start, query };
};
